import { geheimnisseFinden, maskieren } from '@netcon-work/notizbuch-kern'
import { useCallback, useEffect, useRef, useState } from 'react'
import type { SpeichernEingabe } from './daten'

/**
 * **Zwischenspeicher ohne Netz** (Z-23, „Unterwegs" – Nils: Offline-Warteschlange für neue Notizen
 * ist in Ordnung).
 *
 * Nur **neue** Schnellnotizen warten hier – das Bearbeiten bestehender Blätter braucht Netz, sonst
 * gäbe es Konflikte zwischen Fassungen. Was wartet, liegt im Browser dieses Geräts und wird
 * geschickt, sobald wieder Netz da ist (beim Öffnen und bei `online`).
 *
 * **Passwörter landen nicht im Browser**: maskiert wird vor dem Ablegen (E-420), nicht erst beim
 * Senden.
 */

const SCHLUESSEL = 'notizbuch:warteschlange'

export function lesen(): SpeichernEingabe[] {
  try {
    const roh = localStorage.getItem(SCHLUESSEL)
    return roh ? (JSON.parse(roh) as SpeichernEingabe[]) : []
  } catch {
    return []
  }
}

function schreiben(liste: SpeichernEingabe[]): boolean {
  try {
    if (liste.length) localStorage.setItem(SCHLUESSEL, JSON.stringify(liste))
    else localStorage.removeItem(SCHLUESSEL)
    return true
  } catch {
    return false
  }
}

/** Maskiert und legt ab. `false`, wenn der Browser nichts speichern lässt. */
export function einreihen(e: SpeichernEingabe): boolean {
  const sauber = { ...e, titel: maskieren(e.titel), inhalt: maskieren(e.inhalt) }
  return schreiben([...lesen().filter((x) => x.id !== e.id), sauber])
}

/**
 * Schickt, was wartet, mit `senden` – der Reihe nach, bei einem Fehler wird abgebrochen und es beim
 * nächsten Anlass erneut versucht. Liefert, wie viele noch warten.
 */
export function useWarteschlange(senden: (e: SpeichernEingabe) => Promise<unknown>) {
  const [wartend, setWartend] = useState(() => lesen().length)
  const laeuft = useRef(false)
  const sendenRef = useRef(senden)
  sendenRef.current = senden

  const abarbeiten = useCallback(async () => {
    if (laeuft.current) return
    laeuft.current = true
    try {
      for (const e of lesen()) {
        try {
          await sendenRef.current(e)
        } catch {
          break
        }
        schreiben(lesen().filter((x) => x.id !== e.id))
      }
    } finally {
      laeuft.current = false
      setWartend(lesen().length)
    }
  }, [])

  useEffect(() => {
    void abarbeiten()
    window.addEventListener('online', abarbeiten)
    return () => window.removeEventListener('online', abarbeiten)
  }, [abarbeiten])

  const neuGezaehlt = useCallback(() => setWartend(lesen().length), [])
  return { wartend, abarbeiten, neuGezaehlt }
}

/** Gibt es im Text etwas zu maskieren? – für den Hinweis unter dem Feld. */
export function hatGeheimnis(text: string): boolean {
  return geheimnisseFinden(text).length > 0
}
